import type { FC, ReactNode } from 'react';
import React, {
  Children,
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Flex } from '@rover/kibble/core';
import { ThemeHeadingSizes } from '@rover/kibble/styles';
import useOnScrollEnd from '@rover/react-lib/src/hooks/useOnScrollEnd';
import useResizeObserver from '@rover/react-lib/src/hooks/useResizeObserver';

import { Header } from './components/Header';
import Stepper, { PaginateDirection } from './components/Stepper';
import { Props as SnappingCarouselProps } from './SnappingCarousel/v2';
import { CarouselScrollSource, OnPaginateAction } from './utils/onPaginateEvent.types';
import { Props as BaseCarouselProps } from './BaseCarousel';
import { ScrollEventHandler } from './types';

export type Props = {
  /** enforces that the PaginationWrapper only is used with these two Carousels */
  children: React.ReactElement<SnappingCarouselProps> | React.ReactElement<BaseCarouselProps>;
  heading?: string;
  headingSize?: ThemeHeadingSizes;
  subheading?: string | ReactNode;
  onPaginate?: OnPaginateAction;
};

function getCarouselItemWidth(item: HTMLElement, container: HTMLElement): number {
  const { marginLeft = '0', marginRight = '0' } = getComputedStyle(item);
  return (
    item.offsetWidth +
    parseFloat(marginRight) +
    parseFloat(marginLeft) +
    (parseFloat(getComputedStyle(container).gap) || 0) // The container itself has a gap property which affects the item width
  );
}

function isCarouselOnLastPage({ scrollLeft, scrollWidth, clientWidth }: HTMLDivElement): boolean {
  return scrollWidth - scrollLeft === clientWidth;
}

/**
 * A wrapper component that can be used to wrap any carousel that uses `BaseCarousel` to add pagination.
 *
 * ⚠️: This component makes STRONG assumptions that you are using the `BaseCarousel` or `SnappingCarousel` with it.
 *
 * ⚠️: Before modifying this wrapper, please read through the comments to better understand the lifecycle of this component
 */
const PaginationWrapper: FC<Props> = ({
  children,
  heading,
  subheading,
  headingSize,
  onPaginate = () => {},
}: Props): JSX.Element => {
  const [currentPage, setCurrentPage] = useState(1); // Note: this should only ever be called once and only in handleScroll
  const [scrollAmount, setScrollAmount] = useState(0); // This is the carousel pagination size in pixels. It is used to determine how much to scroll when paginating via the Stepper
  const [totalPages, setTotalPages] = useState(1);
  const isScrolling = useRef(false);
  // These two variables are meant to control whether the next or prev button of a stepper is disabled. They are calculated on each scrollend event
  const [isAtStartOfScrollableContent, setIsAtStartOfScrollableContent] = useState(true); // We default to true because on page load we always start at the beginning
  const [isAtEndOfScrollableContent, setIsAtEndOfScrollableContent] = useState(false); // Conversely, we default to false because on page load we never start at the end
  const scrollSource = useRef<CarouselScrollSource | null | 'pageValueRecalculation'>(null); // This is used to discern scroll actions that were triggered by pagination button vs scroll wheel
  const carouselRef = useRef<HTMLDivElement>(null); // We use this carouselRef for calculating page values
  const pageIndexBeforeScroll = useRef(1); // Tracks the page value prior to scrolling/swiping. It should only be updated at the beginning of handScroll

  const carouselScrollContainer = Children.only(children); // This is the ScrollContainer from the carousel. This is used for reference but not calculations
  const carouselItems = carouselScrollContainer.props.children; // These are the carousel items within the ScrollContainer
  const totalItems = carouselItems ? Children.count(carouselItems) : 0;

  /**
   * 🔁
   * Trigger: Page resize
   * Action: It retrieves the first item in the carousel and calculates page values based on its width and the carousel width
   * Lastly it will reset the carousel position after updating these values
   * Used to calculate the total pages and page size for a carousel at any given size.
   *
   * ## Note 1:
   * Pagination values are calculated under the assumption that every item in the carousel has the same width.
   *
   * ----
   *
   * ## Note 2:
   * The values for `itemsPerPage` and `totalPages` are derived from the size of the rendered `DOM` elements, which we can only measure after the component has rendered and the `ref` is attached.
   * Because this calculation must happen in an effect (useEffect or the useResizeObserver callback), we need to store the result in state to trigger a re-render and update the UI with the correct values.
   */
  const calculatePageValues = useCallback(() => {
    const carouselContainer = carouselRef.current;
    const carouselItem = carouselContainer?.firstElementChild;
    if (!carouselItem || !carouselContainer) return;
    // 1. Get the item width of the first carousel item. Note that we assume carousel item is the same size
    const itemWidth = getCarouselItemWidth(carouselItem as HTMLElement, carouselContainer);

    if (itemWidth > 0) {
      // 2. Calculate the itemsPerPage
      const itemsPerPage = Math.max(1, Math.floor(carouselContainer.offsetWidth / itemWidth));

      // 3. Update state
      setScrollAmount(itemWidth * itemsPerPage);
      setTotalPages(Math.ceil(totalItems / itemsPerPage));

      if (carouselRef.current.scrollLeft !== 0) {
        // 4. Set scrollSource to pageValueRecalculation so we know when handling the scroll for windowResizing or props changing
        scrollSource.current = 'pageValueRecalculation';

        // 5. Scroll to position 0 to reset carousel
        carouselRef.current?.scrollTo({ left: 0, behavior: 'auto' });
      }
    }
  }, [totalItems]);
  useResizeObserver(carouselRef, calculatePageValues);

  /**
   * 🔁
   * Trigger: Carousel props change and cause calculatePageValuesOnResize to update its signature
   * Action: Calls calculatePageValuesOnResize to recalculate page values on props change
   */
  useEffect(() => {
    calculatePageValues();
  }, [calculatePageValues]);

  /**
   * 🔁
   * Trigger: scrollend event
   * Action: Clears the scrollSource
   *
   * This hook is to listen in on the scroll-end event. React does not currently support this event directly, so this custom hook handles that event listening for us.
   * Note: If React ever adds onScrollEnd support, I recommend updating this to use that instead of the custom hook
   */
  useOnScrollEnd(carouselRef, () => {
    isScrolling.current = false;
    // 1. Determine if we are at the start or end of the carousel
    if (carouselRef.current) {
      const { scrollLeft } = carouselRef.current;
      setIsAtEndOfScrollableContent(isCarouselOnLastPage(carouselRef.current));
      setIsAtStartOfScrollableContent(scrollLeft === 0);
    }
    // 2. If the scroll action has a source and it is not from the window resizing, call onPaginate
    if (
      scrollSource.current &&
      scrollSource.current !== 'pageValueRecalculation' &&
      pageIndexBeforeScroll.current !== currentPage // We only want to trigger onPaginate if a page change occurred
    ) {
      onPaginate({
        pageStart: pageIndexBeforeScroll.current,
        pageEnd: currentPage,
        scrollSource: scrollSource.current,
      });
    }
    // 3. Reset scrollSource reference once the scroll is complete
    scrollSource.current = null;
  });

  /**
   * 🔁
   * Trigger: When user clicks `<Stepper>` button
   * Action: Triggers a scroll event with the direction set
   */
  const handlePageChange = useCallback(
    (direction: PaginateDirection) => {
      // 1. Set Scroll Source
      scrollSource.current = CarouselScrollSource.BUTTON;
      // 2. Enact a scroll action for the pagination click using requestAnimationFrame to reduce INP impact
      requestAnimationFrame(() => {
        carouselRef?.current?.scrollBy({
          left: scrollAmount * direction,
          behavior: 'smooth',
        });
      });
    },
    [scrollAmount]
  );

  /**
   * 🔁
   * Trigger: ANY scroll event occurs, either by touch or scrollwheel
   * Action: handle the updating of the page index when a user scrolls on the carousel either by scroll wheel or touch swipe interactions.
   */
  const handleScroll = useCallback<ScrollEventHandler>(
    (event) => {
      // 1. Check If scrollSource was not set, we can assume the scroll source came from user swipe/scrolling
      if (scrollSource.current === null) {
        scrollSource.current = CarouselScrollSource.SWIPE;
      }

      // 2. Update the currentPage value while scrolling to make the component dynamically display the current page as you scroll
      if (carouselRef.current) {
        // a. If we just started scrolling, track the pageIndex right before scrolling
        if (!isScrolling.current) {
          isScrolling.current = true;
          pageIndexBeforeScroll.current = currentPage;
        }

        // b. Calculate and update the currentPage as we scroll
        const { scrollLeft } = carouselRef.current;
        const nextPageIndex = isCarouselOnLastPage(carouselRef.current)
          ? totalPages
          : Math.round(scrollLeft / scrollAmount) + 1;
        setCurrentPage(nextPageIndex); // Note: this is the only place setCurrentPage should ever be called
      }

      // 3. If `onScroll` was declared for the wrapped carousel, be sure to call it here otherwise it will get overridden when cloning the element below
      carouselScrollContainer.props.onScroll?.(event);
    },
    [carouselScrollContainer.props, totalPages, scrollAmount, currentPage]
  );

  // cloning creates a copy of a React element and allows us to add new props to it before it gets rendered.
  // Doing so allows us to determine various page values like currentPage, totalPages
  const carouselWithRef = cloneElement(carouselScrollContainer as React.ReactElement<any>, {
    ref: carouselRef,
    onScroll: handleScroll,
  });

  // To reduce complexity and drift between the mobile and desktop Stepper components, I chose to memoize the common props and spread them into each Stepper component
  const sharedStepperProps = useMemo(
    () => ({
      canPaginateNext: !isAtEndOfScrollableContent,
      canPaginatePrevious: !isAtStartOfScrollableContent,
      currentPage: Math.min(currentPage, totalPages),
      totalPages,
      handlePageChange,
    }),
    [
      currentPage,
      handlePageChange,
      isAtEndOfScrollableContent,
      isAtStartOfScrollableContent,
      totalPages,
    ]
  );
  return (
    <Flex width="100%" maxWidth="100vw" flexDirection="column">
      <Flex
        alignItems="center"
        gap="5x"
        flexDirection="row"
        justifyContent={heading || subheading ? 'space-between' : 'flex-end'}
        marginBottom="5x"
        ml="1x"
      >
        <Header heading={heading} headingSize={headingSize} subheading={subheading} />
        {/* Desktop Stepper only displays when display is wide enough */}
        <Stepper
          data-qa-id="desktop-stepper-button-container"
          display={['none', 'flex']}
          {...sharedStepperProps}
        />
      </Flex>
      {/* This is where the passed in Carousel component is rendered */}
      {carouselWithRef}
      {/* Mobile Stepper only displays when display is narrow enough */}
      <Stepper
        data-qa-id="mobile-stepper-button-container"
        mt="4x"
        display={['flex', 'none']}
        {...sharedStepperProps}
      />
    </Flex>
  );
};

export default PaginationWrapper;
